import {
  isDisposalType,
  adjustFromTimeZoneSafely,
  lastOrUndefined,
  PipelineEntity,
  PipelineGraphEdgeEntity,
  PipelineGraphEntity,
  PipelineGraphNodeEntity,
  SiteEntity,
  siteType,
  WellEntity,
  startOfDay,
} from "@ots-frontend/ots-data";
import styled from "@emotion/styled";
import {useCallback, useEffect, useMemo, useState} from "react";
import {IEdge, INode, LayoutEngineType, SelectionT} from "react-digraph";
import {v4 as uuid} from 'uuid';
import {copyRawPipelineGraphViewData} from "../../graph/BasePipelineGraphView/config";
import {BasePipelineGraphView} from "../../graph/BasePipelineGraphView/BasePipelineGraphView";
import {SiteSelectModal} from "../../select/entity/SiteSelect/SiteSelect";
import {WellSelectModal} from "../../select/entity/WellSelect/WellSelect";
import {useDisclosure} from "@mantine/hooks";
import {PipelineGraphBuilderActions} from "../PipelineGraphBuilderActions/PipelineGraphBuilderActions";
import {useTranslation} from "react-i18next";
import {Rectangle} from "@glideapps/glide-data-grid";
import {PipelineGraphForm, PipelineGraphFormValues} from "../../form/PipelineGraphForm/PipelineGraphForm";
import {Card} from "../../card/Card/Card";
import {Title} from "@mantine/core";
import {PipelineGraphNodeCard} from "../../card/PipelineGraphNodeCard/PipelineGraphNodeCard";

export type PipelineGraphBuilderProps = {
  pipeline: PipelineEntity
  pipelineGraph?: PipelineGraphEntity
  newVersion?: boolean
  onSubmit?: (values: PipelineGraphBuilderValues) => void;
  error?: string
  submitting?: boolean;
}

export interface PipelineGraphBuilderValues {
  displayName: string
  effectiveDate: Date
  nodes: PipelineGraphNodeEntity[]
  edges: PipelineGraphEdgeEntity[]
}

type BuilderAction = {
  type: "ADD" | "DELETE" | "EDIT"
  data: {
    node?: INode
    edges?: IEdge[]
  }
}

const GraphContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const PanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  right: 0;
`;

const ActionsContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  left: 0;
`;

const NodePanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  bottom: 0;
  right: 0;
`;

export const PipelineGraphBuilder = (props: PipelineGraphBuilderProps) => {
  const {
    pipeline,
    pipelineGraph: currPipelineGraph,
    newVersion = false,
    error,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation()

  const [layoutEngineType, setLayoutEngineType] = useState<LayoutEngineType>('VerticalTree');

  const [nodes, setNodes] = useState<INode[]>([]);
  const [edges, setEdges] = useState<IEdge[]>([]);

  const [selectedNode, setSelectedNode] = useState<INode | undefined>(undefined);
  const [selectedEdge, setSelectedEdge] = useState<IEdge | undefined>(undefined);
  const [selectedXy, setSelectedXy] = useState<{ x: number; y: number } | undefined>(undefined);

  const siteModalDisclosure = useDisclosure(false);
  const siteModalOpened = siteModalDisclosure[0]
  const siteModalHandlers = siteModalDisclosure[1]

  const wellModalDisclosure = useDisclosure(false);
  const wellModalOpened = wellModalDisclosure[0]
  const wellModalHandlers = wellModalDisclosure[1]

  const [actions, setActions] = useState<BuilderAction[]>([])

  useEffect(() => {
    const rawViewData = copyRawPipelineGraphViewData(
      currPipelineGraph?.nodes,
      currPipelineGraph?.edges,
      typeof currPipelineGraph !== 'undefined' && !newVersion
    );
    setNodes(rawViewData.nodes);
    setEdges(rawViewData.edges);
    setLayoutEngineType('VerticalTree');
  }, [currPipelineGraph, newVersion]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (layoutEngineType === 'VerticalTree') {
        setLayoutEngineType('SnapToGrid');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [layoutEngineType]);

  const onFormSubmit = useCallback((values: PipelineGraphFormValues) => {
    if (onSubmit) {
      const pipelineGraphId = !newVersion ? currPipelineGraph?.id ?? uuid() : uuid();

      const pipelineGraphNodes: PipelineGraphNodeEntity[] = nodes.map((n) =>
        PipelineGraphNodeEntity.builder(n.id)
          .setPipelineGraph(pipelineGraphId)
          .setObject(n.data.objectId, n.data.objectType)
          .setSiteType(n.data.siteType)
          .setBillable(n.data.billable)
          .setComments(n.data.comments)
          .build()
      );

      const pipelineGraphEdges: PipelineGraphEdgeEntity[] = edges.map((e) =>
        PipelineGraphEdgeEntity.builder(e.id)
          .setPipelineGraph(pipelineGraphId)
          .setNodes(e.source, e.target)
          .build()
      );

      onSubmit({
        displayName: values.displayName,
        effectiveDate: values.effectiveDate,
        nodes: pipelineGraphNodes,
        edges: pipelineGraphEdges,
      })
    }
  }, [newVersion, currPipelineGraph?.id, nodes, edges, onSubmit])

  // @ts-ignore
  const onUndoClick = useCallback(() => {
    const action = lastOrUndefined(actions)
    if (action) {
      if (action.type === 'DELETE') {
        if (action.data.node) {
          const updatedNodes = [...nodes]
          updatedNodes.push({...action.data.node})
          setNodes(updatedNodes)
        }
        if (action.data.edges) {
          const newEdges = [...edges]
          newEdges.push(...action.data.edges)
          setEdges(newEdges)
        }
      } else if (action.type === 'ADD') {
        if (action.data.node) {
          setNodes((nodes) => [...(nodes.filter((n) => n.id !== action.data.node!.data.id))])
        }
        if (action.data.edges) {
          setEdges((edges) => {
            const newEdges = [...edges]
            newEdges.push(...action.data.edges!)
            return newEdges
          })
        }
      }
      setActions((actions) => [...actions.splice(-1)])
    }
  }, [actions, nodes, edges]);

  const onFormatClick = useCallback(() => {
    setLayoutEngineType('VerticalTree');
  }, []);

  const onAddActionClick = useCallback(() => {
    if (selectedNode) {
      wellModalHandlers.open()
    } else {
      siteModalHandlers.open()
    }
  }, [selectedNode, wellModalHandlers, siteModalHandlers]);

  const onDeleteActionClick = useCallback(() => {
    if (selectedNode) {
      let updatedNodes: INode[] = [...nodes]
      const deletedNode = updatedNodes.find((n) => n.id === selectedNode.id)
      if (deletedNode) {
        updatedNodes = updatedNodes.filter((n) => n.id !== deletedNode.id)
      }

      let updatedEdges: IEdge[] = [...edges]
      const deletedEdges = updatedEdges.filter((e) => e.source === selectedNode.id || e.target === selectedNode.id)
      if (deletedEdges.length > 0) {
        updatedEdges = updatedEdges.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id)
      }

      if (deletedNode) {
        setNodes(updatedNodes)
      }

      if (deletedEdges.length > 0) {
        setEdges(updatedEdges)
      }

      setSelectedNode(undefined);

      if (deletedNode || deletedEdges.length > 0) {
        const data = {
          node: deletedNode,
          edges: deletedEdges.length > 0 ? [...deletedEdges] : undefined
        }

        setActions((actions) =>
          [...actions, {type: 'DELETE', data: data}]
        )
      }
    }

    if (selectedEdge) {
      let updatedEdges: IEdge[] = [...edges]
      const deletedEdge = updatedEdges.find((n) => n.id === selectedEdge.id)
      if (deletedEdge) {
        updatedEdges = updatedEdges.filter((e) => e.id !== deletedEdge.id)
      }

      if (deletedEdge) {
        setEdges(updatedEdges)
        setActions((actions) =>
          [...actions, {type: 'DELETE', data: {edges: [deletedEdge]}}]
        )
      }

      setSelectedEdge(undefined);
    }
  }, [selectedNode, selectedEdge, nodes, edges]);

  const onAddSite = useCallback((sites: SiteEntity[] | undefined) => {
      if (sites) {
        for (const site of sites) {
          setNodes((nodes) => {
            const alreadyIncluded = nodes.findIndex((n) => n.data.objectId === site.id) >= 0;
            if (!alreadyIncluded) {
              const newNode: INode = {
                id: uuid(),
                title: site.name,
                type: isDisposalType(site.type) ? 'disposal' : 'empty',
                x: selectedXy?.x ? selectedXy.x + 50 : undefined,
                y: selectedXy?.y ? selectedXy.y + 50 : undefined,
                data: {
                  objectId: site.id,
                  objectType: site.entityType,
                  siteType: site.type,
                },
              }

              setActions((actions) =>
                [...actions, {type: 'ADD', data: {node: {...newNode}}}]
              )

              return [
                ...nodes,
                {...newNode},
              ];
            }

            return [...nodes];
          });
        }
      }
      siteModalHandlers.close()
    },
    [selectedXy, siteModalHandlers]
  );

  const onAddWells = useCallback((wells: WellEntity[] | undefined) => {
      if (wells) {
        for (const well of wells) {
          // add node and also add edge from well to its site
          let wellToSiteEdge: IEdge | undefined = undefined;

          setNodes((nodes) => {
            const alreadyIncluded =
              nodes.findIndex((n) => n.data.objectId === well.id) >= 0;

            if (!alreadyIncluded) {
              const siteNode: INode | undefined = nodes.find(
                (n) => n.data.objectId === well.siteId
              );
              if (siteNode) {
                const wellNode: INode = {
                  id: uuid(),
                  title: well.name,
                  type: 'well',
                  x: selectedXy?.x ? selectedXy.x + 50 : undefined,
                  y: selectedXy?.y ? selectedXy.y + 50 : undefined,
                  data: {
                    objectId: well.id,
                    objectType: well.entityType,
                  },
                };

                wellToSiteEdge = {
                  id: uuid(),
                  source: wellNode.id,
                  target: siteNode.id,
                  type: 'emptyEdge',
                };

                setActions((actions) =>
                  [...actions, {type: 'ADD', data: {node: {...wellNode}, edges: [{...wellToSiteEdge!}]}}]
                )

                return [...nodes, {...wellNode}];
              }
            }

            return [...nodes];
          });

          setEdges((edges) => {
            if (wellToSiteEdge) {
              return [...edges, wellToSiteEdge];
            }
            return [...edges];
          });
        }
      }

      wellModalHandlers.close()
    },
    [selectedXy, wellModalHandlers]
  );

  const onCreateEdge = useCallback((sourceNode: INode, targetNode: INode) => {
    if (sourceNode.id !== targetNode.id) {
      setEdges((edgeState) => {
        const newEdge: IEdge = {
          id: uuid(),
          source: sourceNode.id,
          target: targetNode.id,
          type: 'emptyEdge',
        }

        setActions((actions) =>
          [...actions, {type: 'ADD', data: {edges: [{...newEdge}]}}]
        )

        return [
          ...edgeState,
          {...newEdge},
        ];
      });
    }
  }, []);

  const canCreateEdge = useCallback(
    (startNode: INode | undefined, endNode: INode | undefined): boolean => {
      // we do not want users to manually create connections from wells to sites
      if (startNode) {
        if (startNode.type === 'well') {
          return false;
        }
      }

      if (startNode && endNode) {
        if (startNode.type === 'well' && endNode.type === 'well') {
          return false;
        }
      }

      return true;
    },
    []
  );

  const onGraphSelect = useCallback((selection: SelectionT) => {
    const selectedNodes = selection.nodes;
    const selectedEdges = selection.edges;

    let nodeSelection: INode | undefined = undefined;
    let edgeSelection: IEdge | undefined = undefined;

    if (selectedNodes) {
      const [selection] = selectedNodes.values();
      if (selection) {
        nodeSelection = {...selection};
        if (selection.x && selection.y) {
          setSelectedXy({
            x: selection.x,
            y: selection.y,
          });
        }
      }
    }

    if (selectedEdges) {
      const [selection] = selectedEdges.values();
      if (selection) {
        edgeSelection = {...selection};
      }
    }

    setSelectedNode(nodeSelection);
    setSelectedEdge(edgeSelection);
  }, []);

  const onBackgroundClick = useCallback((x: number, y: number, event: any) => {
      setSelectedXy({x, y});
    },
    [setSelectedXy]
  );

  const selectedSiteIdForNode = useMemo(() => {
    return selectedNode?.data?.objectId;
  }, [selectedNode]);

  const selectedNodes = useMemo(() => {
    return selectedNode ? new Map<string, INode>([[selectedNode.id, selectedNode]]) : null
  }, [selectedNode])

  const selectedEdges = useMemo(() => {
    return selectedEdge ? new Map<string, IEdge>([[selectedEdge.id, selectedEdge]]) : null
  }, [selectedEdge])

  return (
    <GraphContainer>
      <BasePipelineGraphView
        nodes={nodes}
        edges={edges}
        selected={{
          nodes: selectedNodes,
          edges: selectedEdges,
        }}
        layoutEngineType={layoutEngineType}
        allowMultiselect={false}
        onSelect={onGraphSelect}
        onCreateEdge={onCreateEdge}
        canCreateEdge={canCreateEdge}
        onBackgroundClick={onBackgroundClick}
      />
      <PanelContainer>
        <Card shadow="sm" padding="lg" radius="md">
          <Title order={3}>
            {newVersion ? "New Pipeline Graph" : "Edit Pipeline Graph"}
          </Title>
          <Title order={5}>
            {`Pipeline: ${pipeline.name}`}
          </Title>
          {error && "Test"}
          <PipelineGraphForm
            defaultValues={{
              displayName: newVersion ? undefined : currPipelineGraph?.displayableName,
              effectiveDate: newVersion ? startOfDay(new Date()) : adjustFromTimeZoneSafely(currPipelineGraph?.effectiveDttm),
            }}
            submitting={submitting}
            onSubmit={onFormSubmit}/>
        </Card>
      </PanelContainer>
      <ActionsContainer>
        <PipelineGraphBuilderActions
          onAddClick={onAddActionClick}
          // onEditClick={selectedNode ? onEditActionClick : undefined}
          onDeleteClick={selectedNode || selectedEdge ? onDeleteActionClick : undefined}
          // onUndoClick={onUndoClick}
          onFormatClick={onFormatClick}
        />
      </ActionsContainer>
      <SiteSelectModal
        select={{
          withinPortal: true,
        }}
        title={t<string>("common.entity.site", "Site")}
        size="lg"
        opened={siteModalOpened}
        onClose={siteModalHandlers.close}
        onSubmit={onAddSite}
      />
      <WellSelectModal
        select={{
          withinPortal: true,
          multiple: true,
          selector: {
            siteId: selectedSiteIdForNode
          }
        }}
        title={t<string>("common.entity.well", "Well")}
        size="lg"
        opened={wellModalOpened}
        onClose={() => {
          wellModalHandlers.close()
          setSelectedNode(undefined);
        }}
        onSubmit={onAddWells}
      />
      {selectedNode &&
        <NodePanelContainer>
          <PipelineGraphNodeCard
            node={selectedNode}/>
        </NodePanelContainer>
      }
    </GraphContainer>
  );
}
